import { Fragment } from 'preact';
import { useCallback } from 'preact/hooks';

import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { Flex } from '../UNSAFE_Flex';
import { Divider } from '../UNSAFE_Divider';
import { CollapsibleHeaderIcon } from './CollapsibleHeaderIcon';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { usePress } from '../hooks/UNSAFE_usePress';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'id' | 'children'>;

type HeaderProps = IntrinsicProps & {
  id: string;
  contentId: string;
  isDisabled: boolean;
  isExpanded: boolean;
  iconPosition?: 'start' | 'end';
  variant?: 'basic' | 'horizontal-rule';
  toggleHandler: (target: EventTarget | null) => void;
};

const styles = {
  base: cssProps`${{
    cursor: 'pointer',
    '&:focus-visible': {
      outlineStyle: 'dotted',
      outlineOffset: '1px',
      outlineWidth: '1px',
      outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)'
    }
  }}`,
  expanded: cssProps`${{
    paddingBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-1x)'
  }}`,
  disabled: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-disabled)',
    cursor: 'auto'
  }}`,
  divider: cssProps`${{
    paddingBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)'
  }}`
};

const childrenStylesBase = {
  base: cssProps`${{
    flexGrow: 1,
    /* @ts-ignore */
    '& > *': {
      marginTop: '0 !important',
      marginBottom: '0 !important',
      marginLeft: '0 !important',
      marginRight: '0 !important'
    }
  }}`,
  iconStart: cssProps`${{
    paddingInlineStart: '16px'
  }}`,
  iconEnd: cssProps`${{
    paddingInlineEnd: '16px'
  }}`
};

/**
 * Header subcomponent
 */

export const CollapsibleHeader = ({
  children,
  id,
  contentId,
  isDisabled,
  isExpanded,
  iconPosition,
  variant = 'basic',
  toggleHandler
}: HeaderProps) => {
  const classes = classNames([
    styles.base,
    variant === 'horizontal-rule' && styles.divider,
    isExpanded && variant !== 'horizontal-rule' && styles.expanded,
    isDisabled && styles.disabled
  ]);

  const childrenClasses = classNames([
    childrenStylesBase.base,
    iconPosition === 'end' && childrenStylesBase.iconEnd,
    iconPosition === 'start' && childrenStylesBase.iconStart
  ]);

  const pressHandler = useCallback(
    (event: Event) => {
      toggleHandler(event.target);
    },
    [toggleHandler]
  );

  const { pressProps } = usePress(pressHandler, {
    isDisabled,
    isRepeat: false
  });

  const getHeader = () => (
    <Fragment>
      {iconPosition === 'end' && <div className={childrenClasses}>{children}</div>}
      <CollapsibleHeaderIcon iconPosition={iconPosition} isExpanded={isExpanded} />
      {iconPosition === 'start' && <div className={childrenClasses}>{children}</div>}
    </Fragment>
  );

  return (
    <>
      <div
        role={'button'}
        aria-controls={contentId}
        aria-expanded={isExpanded}
        id={id}
        tabIndex={0}
        className={classes}
        {...pressProps}>
        <Flex align="center" justify={iconPosition === 'start' ? 'start' : 'between'}>
          {getHeader()}
        </Flex>
      </div>
      {variant === 'horizontal-rule' && <Divider />}
    </>
  );
};
